import React from 'react';
import { motion } from 'framer-motion';
import { Database, Cloud, LayoutGrid, ShieldCheck, Share2, PieChart, ArrowRight, Activity, Server, Lock } from 'lucide-react';
export function ArchitectureDiagram() {
  return <section className="relative w-full bg-[#16120F] py-24 md:py-32 overflow-hidden min-h-[900px] flex items-center" data-id="element-175">
      {/* Background SVG Lines */}
      <div className="absolute inset-0 z-0 opacity-60" data-id="element-176">
        <svg className="w-full h-full" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" data-id="element-177">
          <defs data-id="element-178">
            <linearGradient id="glow" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-179">
              <stop offset="0%" stopColor="#EA580C" stopOpacity="0" data-id="element-180" />
              <stop offset="50%" stopColor="#EA580C" stopOpacity="1" data-id="element-181" />
              <stop offset="100%" stopColor="#EA580C" stopOpacity="0" data-id="element-182" />
            </linearGradient>
            <filter id="blur" data-id="element-183">
              <feGaussianBlur stdDeviation="4" data-id="element-184" />
            </filter>
          </defs>

          {/* Base Lines */}
          <path d="M -100 400 L 200 400 L 300 200 L 500 200 L 600 400 L 800 400" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-185" />
          <path d="M 500 200 L 700 200 L 800 100 L 1000 100" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-186" />
          <path d="M 800 400 L 1000 400 L 1100 600 L 1300 600" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-187" />
          <path d="M 400 700 L 600 700 L 700 500 L 800 500" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-188" />
          <path d="M 800 500 L 800 700 L 1000 700" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-189" />

          {/* Animated Glowing Lines */}
          <motion.path d="M -100 400 L 200 400 L 300 200 L 500 200" fill="none" stroke="url(#glow)" strokeWidth="3" filter="url(#blur)" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-190" />
          <motion.path d="M 500 200 L 700 200 L 800 100 L 1000 100" fill="none" stroke="url(#glow)" strokeWidth="3" filter="url(#blur)" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear',
          delay: 1
        }} data-id="element-191" />
          <motion.path d="M 800 400 L 1000 400 L 1100 600 L 1300 600" fill="none" stroke="url(#glow)" strokeWidth="3" filter="url(#blur)" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear',
          delay: 2
        }} data-id="element-192" />
        </svg>
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 w-full grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-12" data-id="element-193">
        {/* Left Content */}
        <div className="flex flex-col justify-center pt-10 lg:pt-0" data-id="element-194">
          <h2 className="text-5xl md:text-7xl font-title font-medium tracking-tight text-white leading-[1.05] mb-6" data-id="element-195">
            Connect
            <br data-id="element-196" />
            <span className="text-[#EA580C]" data-id="element-197">Everything.</span>
            <br data-id="element-198" />
            Architect
            <br data-id="element-199" />
            Anything.
          </h2>
          <p className="text-lg text-[#9C8E82] leading-relaxed mb-10 max-w-sm" data-id="element-200">
            The intelligent infrastructure platform that connects your data,
            systems, and teams—seamlessly.
          </p>
          <div className="flex items-center gap-6" data-id="element-201">
            <button className="px-6 py-3 rounded-full bg-[#EA580C] hover:bg-[#d24e09] text-white font-semibold transition-colors flex items-center gap-2" data-id="element-202">
              Request a Demo
              <ArrowRight size={18} data-id="element-203" />
            </button>
            <button className="text-[#EA580C] font-medium hover:text-white transition-colors flex items-center gap-2" data-id="element-204">
              Explore Platform
              <ArrowRight size={18} data-id="element-205" />
            </button>
          </div>
        </div>

        {/* Right Diagram Area */}
        <div className="relative h-[800px] hidden lg:block" data-id="element-206">
          {/* Data Sources Card */}
          <div className="absolute top-[50px] left-[100px] w-[280px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-207">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-208">
              <Database size={14} className="text-[#EA580C]" data-id="element-209" />
              Data Sources
            </div>
            <div className="space-y-2" data-id="element-210">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-211">
                <Cloud size={18} className="text-neutral-300" data-id="element-212" />
                <div data-id="element-213">
                  <div className="text-sm font-medium text-white" data-id="element-214">
                    Cloud Storage
                  </div>
                  <div className="text-xs text-neutral-500" data-id="element-215">Amazon S3</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-216">
                <Server size={18} className="text-neutral-300" data-id="element-217" />
                <div data-id="element-218">
                  <div className="text-sm font-medium text-white" data-id="element-219">Database</div>
                  <div className="text-xs text-neutral-500" data-id="element-220">PostgreSQL</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-221">
                <LayoutGrid size={18} className="text-neutral-300" data-id="element-222" />
                <div data-id="element-223">
                  <div className="text-sm font-medium text-white" data-id="element-224">
                    Applications
                  </div>
                  <div className="text-xs text-neutral-500" data-id="element-225">12 Connectors</div>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-226">
              View all sources <ArrowRight size={12} data-id="element-227" />
            </div>
          </div>

          {/* Data Warehouse Card */}
          <div className="absolute top-[250px] left-[450px] w-[300px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl z-20" data-id="element-228">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-229">
              <Database size={14} className="text-[#EA580C]" data-id="element-230" />
              Data Warehouse
            </div>
            <div className="mb-6" data-id="element-231">
              <div className="text-sm font-medium text-white" data-id="element-232">
                Analytics Warehouse
              </div>
              <div className="text-xs text-neutral-500" data-id="element-233">Snowflake</div>
            </div>
            {/* Hex Grid Graphic Placeholder */}
            <div className="h-32 flex items-center justify-center mb-4" data-id="element-234">
              <div className="grid grid-cols-5 gap-2 opacity-80" data-id="element-235">
                {Array.from({
                length: 25
              }).map((_, i) => <div key={i} className={`w-3 h-3 rounded-full ${i % 3 === 0 ? 'bg-[#EA580C]' : 'bg-white/20'}`} data-id="element-236" />)}
              </div>
            </div>
            <div className="flex items-center justify-between text-xs" data-id="element-237">
              <div className="flex items-center gap-1.5 text-emerald-400" data-id="element-238">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" data-id="element-239" />
                Healthy
              </div>
              <div className="text-neutral-500" data-id="element-240">99.9% uptime</div>
            </div>
          </div>

          {/* Data Pipelines Card */}
          <div className="absolute top-[40px] right-[20px] w-[280px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-241">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-242">
              <Activity size={14} className="text-[#EA580C]" data-id="element-243" />
              Data Pipelines
            </div>
            <div className="space-y-2" data-id="element-244">
              <div className="p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-245">
                <div className="text-sm font-medium text-white mb-1" data-id="element-246">
                  User Activity Pipeline
                </div>
                <div className="flex items-center justify-between" data-id="element-247">
                  <span className="text-xs text-[#EA580C]" data-id="element-248">Real-time</span>
                  <svg width="60" height="15" viewBox="0 0 60 15" fill="none" data-id="element-249">
                    <path d="M0 10 Q 10 0, 20 10 T 40 10 T 60 5" stroke="#EA580C" strokeWidth="1.5" fill="none" data-id="element-250" />
                  </svg>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-251">
                <div className="text-sm font-medium text-white mb-1" data-id="element-252">
                  ETL – Sales Data
                </div>
                <div className="flex items-center justify-between" data-id="element-253">
                  <span className="text-xs text-blue-400" data-id="element-254">Scheduled</span>
                  <svg width="60" height="15" viewBox="0 0 60 15" fill="none" data-id="element-255">
                    <path d="M0 5 Q 15 15, 30 5 T 60 10" stroke="#60A5FA" strokeWidth="1.5" fill="none" data-id="element-256" />
                  </svg>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-257">
                <div className="text-sm font-medium text-white mb-1" data-id="element-258">
                  Event Processing
                </div>
                <div className="flex items-center justify-between" data-id="element-259">
                  <span className="text-xs text-[#EA580C]" data-id="element-260">Streaming</span>
                  <svg width="60" height="15" viewBox="0 0 60 15" fill="none" data-id="element-261">
                    <path d="M0 12 Q 10 2, 20 12 T 40 8 T 60 12" stroke="#EA580C" strokeWidth="1.5" fill="none" data-id="element-262" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-263">
              View all pipelines <ArrowRight size={12} data-id="element-264" />
            </div>
          </div>

          {/* Governance Card */}
          <div className="absolute bottom-[100px] left-[150px] w-[260px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-265">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-266">
              <ShieldCheck size={14} className="text-[#EA580C]" data-id="element-267" />
              Governance
            </div>
            <div className="space-y-4" data-id="element-268">
              <div data-id="element-269">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-270">
                  <span className="text-white" data-id="element-271">Data Quality</span>
                  <span className="text-neutral-400" data-id="element-272">98%</span>
                </div>
                <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-273">
                  <div className="h-full bg-[#EA580C] w-[98%]" data-id="element-274" />
                </div>
              </div>
              <div data-id="element-275">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-276">
                  <span className="text-white" data-id="element-277">Compliance</span>
                  <span className="text-neutral-400" data-id="element-278">100%</span>
                </div>
                <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-279">
                  <div className="h-full bg-[#EA580C] w-full" data-id="element-280" />
                </div>
              </div>
              <div data-id="element-281">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-282">
                  <span className="text-white" data-id="element-283">Access Control</span>
                  <span className="text-emerald-400" data-id="element-284">Active</span>
                </div>
                <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-285">
                  <div className="h-full bg-emerald-400 w-full" data-id="element-286" />
                </div>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-287">
              View governance <ArrowRight size={12} data-id="element-288" />
            </div>
          </div>

          {/* Integrations Card */}
          <div className="absolute bottom-[40px] left-[480px] w-[240px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-289">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-290">
              <Share2 size={14} className="text-[#EA580C]" data-id="element-291" />
              Integrations
            </div>
            <div className="flex items-center gap-3 mb-4" data-id="element-292">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-293">
                <img src="https://cdn.simpleicons.org/slack/white" alt="Slack" className="w-5 h-5" data-id="element-294" />
              </div>
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-295">
                <img src="https://cdn.simpleicons.org/salesforce/white" alt="Salesforce" className="w-5 h-5" data-id="element-296" />
              </div>
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-297">
                <LayoutGrid size={18} className="text-white" data-id="element-298" />
              </div>
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-299">
                <Cloud size={18} className="text-white" data-id="element-300" />
              </div>
            </div>
            <div className="flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-301">
              Manage integrations <ArrowRight size={12} data-id="element-302" />
            </div>
          </div>

          {/* Analytics & BI Card */}
          <div className="absolute bottom-[120px] right-[40px] w-[260px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-303">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-304">
              <PieChart size={14} className="text-[#EA580C]" data-id="element-305" />
              Analytics & BI
            </div>
            <div className="space-y-2" data-id="element-306">
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/5" data-id="element-307">
                <span className="text-sm text-white" data-id="element-308">Executive Dashboard</span>
                <div className="flex items-end gap-0.5 h-4" data-id="element-309">
                  <div className="w-1 bg-neutral-500 h-[40%]" data-id="element-310" />
                  <div className="w-1 bg-neutral-500 h-[70%]" data-id="element-311" />
                  <div className="w-1 bg-neutral-500 h-[50%]" data-id="element-312" />
                  <div className="w-1 bg-[#EA580C] h-[100%]" data-id="element-313" />
                </div>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/5" data-id="element-314">
                <span className="text-sm text-white" data-id="element-315">Customer Insights</span>
                <div className="flex items-end gap-0.5 h-4" data-id="element-316">
                  <div className="w-1 bg-[#EA580C] h-[80%]" data-id="element-317" />
                  <div className="w-1 bg-neutral-500 h-[40%]" data-id="element-318" />
                  <div className="w-1 bg-neutral-500 h-[60%]" data-id="element-319" />
                  <div className="w-1 bg-neutral-500 h-[30%]" data-id="element-320" />
                </div>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/5" data-id="element-321">
                <span className="text-sm text-white" data-id="element-322">Reports & Exports</span>
                <div className="flex items-end gap-0.5 h-4" data-id="element-323">
                  <div className="w-1 bg-neutral-500 h-[30%]" data-id="element-324" />
                  <div className="w-1 bg-neutral-500 h-[50%]" data-id="element-325" />
                  <div className="w-1 bg-[#EA580C] h-[90%]" data-id="element-326" />
                  <div className="w-1 bg-neutral-500 h-[60%]" data-id="element-327" />
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-328">
              View all dashboards <ArrowRight size={12} data-id="element-329" />
            </div>
          </div>

          {/* Floating Icons / Nodes */}
          <div className="absolute top-[180px] left-[250px] w-10 h-10 rounded-full bg-[#EA580C]/20 border border-[#EA580C] flex items-center justify-center shadow-[0_0_20px_rgba(234,88,12,0.4)]" data-id="element-330">
            <Database size={16} className="text-[#EA580C]" data-id="element-331" />
          </div>
          <div className="absolute top-[200px] right-[320px] w-10 h-10 rounded-full bg-white/10 border border-white/30 flex items-center justify-center" data-id="element-332">
            <Lock size={16} className="text-white" data-id="element-333" />
          </div>
          <div className="absolute bottom-[300px] right-[280px] w-10 h-10 rounded-full bg-[#EA580C]/20 border border-[#EA580C] flex items-center justify-center shadow-[0_0_20px_rgba(234,88,12,0.4)]" data-id="element-334">
            <PieChart size={16} className="text-[#EA580C]" data-id="element-335" />
          </div>
          <div className="absolute bottom-[250px] left-[420px] w-12 h-12 rounded-full bg-[#EA580C] flex items-center justify-center shadow-[0_0_30px_rgba(234,88,12,0.6)] z-30" data-id="element-336">
            <Share2 size={20} className="text-white" data-id="element-337" />
          </div>
          <div className="absolute bottom-[280px] left-[80px] w-8 h-8 rounded-full bg-[#1A1410] border border-neutral-600 flex items-center justify-center z-30" data-id="element-338">
            <Lock size={12} className="text-neutral-400" data-id="element-339" />
          </div>
        </div>
      </div>
    </section>;
}